import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SgoShell } from "@/components/SgoShell";
import { toast } from "sonner";
import { Search, Save, Loader2 } from "lucide-react";
import { orc, sgo } from "@/lib/api";

const SECOES = [
  { id: "contratante", label: "Dados do Contratante" },
  { id: "objeto", label: "Objeto & Vigência" },
  { id: "identificacao", label: "Identificação do Orçamento" },
  { id: "parametros", label: "Parâmetros Globais" },
];

const ALIQUOTAS = [
  { pct: 0.65, sigla: "PIS" }, { pct: 3.00, sigla: "COFINS" }, { pct: 3.00, sigla: "ISS" },
  { pct: 4.00, sigla: "IRPJ" }, { pct: 2.88, sigla: "CSLL" }, { pct: 0.67, sigla: "OUTROS" },
  { pct: 15.00, sigla: "LUCRO" }, { pct: 70.00, sigla: "CUSTO/OP" },
];

export default function SgoNovoOrcamento() {
  const [params] = useSearchParams();
  const sec = params.get("sec") || "contratante";
  const [form, setForm] = useState({
    cnpj: "", razao_social: "", nome_fantasia: "", porte: "", situacao_cadastral: "",
    logradouro: "", numero: "", complemento: "", cep: "", bairro: "", municipio: "", uf: "SP",
    telefone: "", cnae_principal: "",
    ramo_atividade: "",
    lider_nome: "", lider_tel: "", lider_email: "", lider_sigla: "",
    regional_nome: "", regional_tel: "", regional_email: "", regional_sigla: "",
    objeto_contrato: "", tipo_vigencia: "mensalista", escala_diarias: "",
    data_inicio: "", duracao_meses: 12,
    numero_orcamento: `ORC-${new Date().getFullYear()}-001`,
    operador: "",
    overhead_pct: 20, margem_lucro_pct: 15,
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const handle = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const consultarRF = async () => {
    if (!form.cnpj) return;
    toast.info("Consulta RF (mock) — em produção usa API BrasilAPI");
    handle("razao_social", form.razao_social || "JUST FIT PARTICIPACOES EM EMPREENDIMENTOS S.A.");
    handle("nome_fantasia", form.nome_fantasia || "Just Fit");
    handle("municipio", form.municipio || "SAO PAULO");
    handle("porte", "Grande Empresa");
    handle("situacao_cadastral", "ATIVA");
    handle("cnae_principal", "9313-1/00");
  };

  const buscarCliente = async () => {
    try {
      const { data } = await sgo.clientes();
      if (data[0]) {
        const c = data[0];
        handle("cnpj", c.cnpj); handle("razao_social", c.razao_social);
        handle("nome_fantasia", c.nome_fantasia); handle("municipio", c.municipio); handle("uf", c.uf);
        handle("lider_nome", c.lider); handle("lider_tel", c.lider_contato);
        toast.success(`Cliente vinculado: ${c.razao_social}`);
      }
    } catch { toast.error("Erro ao buscar clientes"); }
  };

  const salvar = async () => {
    if (!form.cnpj || !form.razao_social) { toast.error("Preencha CNPJ e Razão Social"); return; }
    setSaving(true);
    try {
      await orc.criar({ cliente_nome: form.razao_social, uf: form.uf, observacoes: form.objeto_contrato });
      toast.success("Orçamento criado");
      navigate("/sgo/orcamentos");
    } catch { toast.error("Erro"); } finally { setSaving(false); }
  };

  return (
    <SgoShell activeModule={`orc-${sec === "contratante" ? "contratante" : sec}`}>
      <div className="p-5 md:p-8 fade-up grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
        <div className="space-y-5">
          {/* Vincular cliente */}
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-[#1A1D42]">Vincular cliente cadastrado</div>
                <div className="text-xs text-[#6B6E8F] mt-0.5">Preenche todos os campos automaticamente</div>
              </div>
              <button onClick={buscarCliente} data-testid="btn-buscar-cliente" className="bg-[#1A1D42] hover:bg-[#252952] text-[#B28F4B] px-4 py-2.5 rounded-lg font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2 border border-[#B28F4B]/40">
                <Search size={13}/> Buscar Cliente
              </button>
            </div>
          </Card>

          {/* Dados do Contratante */}
          <Card title="Dados do Contratante — Receita Federal" accent="#818CF8">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-3">
              <F label="CNPJ do Contratante"><input value={form.cnpj} onChange={e => handle("cnpj", e.target.value)} placeholder="00.000.000/0000-00" className="inp-l" data-testid="input-cnpj-contratante"/></F>
              <div className="flex items-end">
                <button onClick={consultarRF} className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2.5 rounded-lg font-bold text-xs uppercase tracking-widest">⚡ Consultar RF</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <F label="Nome Empresarial (Razão Social)"><input value={form.razao_social} onChange={e => handle("razao_social", e.target.value)} placeholder="Preenchido automaticamente via CNPJ" className="inp-l"/></F>
              <F label="Nome Fantasia"><input value={form.nome_fantasia} onChange={e => handle("nome_fantasia", e.target.value)} placeholder="Preenchido automaticamente via CNPJ" className="inp-l"/></F>
              <F label="Porte da Empresa"><input value={form.porte} onChange={e => handle("porte", e.target.value)} placeholder="ME, MEI, EPP..." className="inp-l"/></F>
              <F label="Situação Cadastral"><input value={form.situacao_cadastral} onChange={e => handle("situacao_cadastral", e.target.value)} placeholder="Preenchido via CNPJ" className="inp-l"/></F>
            </div>
            <div className="grid grid-cols-[1fr_120px_140px] gap-3 mt-3">
              <F label="Logradouro (Rua/Av)"><input value={form.logradouro} onChange={e => handle("logradouro", e.target.value)} className="inp-l"/></F>
              <F label="Nº"><input value={form.numero} onChange={e => handle("numero", e.target.value)} className="inp-l"/></F>
              <F label="Complemento"><input value={form.complemento} onChange={e => handle("complemento", e.target.value)} className="inp-l"/></F>
            </div>
            <div className="grid grid-cols-[130px_1fr_1fr_90px] gap-3 mt-3">
              <F label="CEP"><input value={form.cep} onChange={e => handle("cep", e.target.value)} placeholder="00000-000" className="inp-l"/></F>
              <F label="Bairro / Distrito"><input value={form.bairro} onChange={e => handle("bairro", e.target.value)} className="inp-l"/></F>
              <F label="Município"><input value={form.municipio} onChange={e => handle("municipio", e.target.value)} className="inp-l"/></F>
              <F label="UF"><input value={form.uf} onChange={e => handle("uf", e.target.value.toUpperCase().slice(0,2))} className="inp-l"/></F>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <F label="Telefone"><input value={form.telefone} onChange={e => handle("telefone", e.target.value)} placeholder="(00) 00000-0000" className="inp-l"/></F>
              <F label="CNAE Principal (Receita)"><input value={form.cnae_principal} onChange={e => handle("cnae_principal", e.target.value)} className="inp-l"/></F>
            </div>
          </Card>

          {/* Ramo Atividade */}
          <Card title="Ramo de Atividade do Contratante" accent="#818CF8">
            <F label="Selecione o Ramo de Atividade (CNAE) do Cliente">
              <select value={form.ramo_atividade} onChange={e => handle("ramo_atividade", e.target.value)} className="inp-l">
                <option value="">Selecione o ramo da atividade...</option>
                <option value="condominio">Condomínio Residencial/Comercial</option>
                <option value="industria">Indústria</option>
                <option value="comercio">Comércio Varejista</option>
                <option value="hospital">Hospital/Clínica</option>
                <option value="escola">Escola/Universidade</option>
              </select>
            </F>
          </Card>

          {/* Contatos Responsáveis */}
          <Card title="Contatos Responsáveis" accent="#F59E0B">
            <div className="text-[10px] font-bold text-[#B28F4B] uppercase tracking-widest mb-2">Líder / Responsável pelo Contrato</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <F label="Nome do Líder"><input value={form.lider_nome} onChange={e => handle("lider_nome", e.target.value)} className="inp-l"/></F>
              <F label="Telefone do Líder"><input value={form.lider_tel} onChange={e => handle("lider_tel", e.target.value)} placeholder="(00) 00000-0000" className="inp-l"/></F>
              <F label="E-mail do Líder"><input value={form.lider_email} onChange={e => handle("lider_email", e.target.value)} className="inp-l"/></F>
              <F label="Sigla"><input value={form.lider_sigla} onChange={e => handle("lider_sigla", e.target.value)} placeholder="Ex: KSS" className="inp-l"/></F>
            </div>
            <div className="text-[10px] font-bold text-[#B28F4B] uppercase tracking-widest mb-2 mt-4">Regional / Supervisor de Área</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <F label="Nome do Regional"><input value={form.regional_nome} onChange={e => handle("regional_nome", e.target.value)} className="inp-l"/></F>
              <F label="Telefone do Regional"><input value={form.regional_tel} onChange={e => handle("regional_tel", e.target.value)} placeholder="(00) 00000-0000" className="inp-l"/></F>
              <F label="E-mail do Regional"><input value={form.regional_email} onChange={e => handle("regional_email", e.target.value)} className="inp-l"/></F>
              <F label="Sigla Regional"><input value={form.regional_sigla} onChange={e => handle("regional_sigla", e.target.value)} placeholder="Ex: RGO" className="inp-l"/></F>
            </div>
          </Card>

          {/* Objeto e Vigência */}
          <Card title="Objeto do Contrato e Vigência" accent="#10B981">
            <F label="Objeto do Contrato / CNAE SOEASY">
              <select value={form.objeto_contrato} onChange={e => handle("objeto_contrato", e.target.value)} className="inp-l">
                <option value="">Selecione o objeto do contrato...</option>
                <option value="80.11-1-01">80.11-1-01 · Vigilância e segurança privada</option>
                <option value="81.21-4-00">81.21-4-00 · Limpeza em prédios</option>
                <option value="82.11-3-00">82.11-3-00 · Serviços administrativos</option>
              </select>
            </F>
            <div className="mt-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B6E8F] mb-2">Tipo de Vigência do Contrato</div>
              <div className="flex gap-2">
                {["diarista", "mensalista", "anual"].map(t => (
                  <button key={t} onClick={() => handle("tipo_vigencia", t)} className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest ${form.tipo_vigencia === t ? "bg-cyan-100 border-2 border-cyan-500 text-cyan-700" : "bg-slate-100 border border-slate-300 text-[#6B6E8F]"}`}>
                    {t === "diarista" ? "🗓 Diarista" : t === "mensalista" ? "📅 Mensalista" : "📆 Anual"}
                  </button>
                ))}
              </div>
            </div>
            <F label="Escala de Serviço para Diárias">
              <select value={form.escala_diarias} onChange={e => handle("escala_diarias", e.target.value)} className="inp-l mt-3">
                <option value="">Selecione a escala de serviço...</option>
                <option>12x36</option><option>24x48</option><option>6x1</option><option>5x2</option>
              </select>
            </F>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mt-3 text-xs text-amber-800">
              ⚠️ Sábados, Domingos e Feriados = 2× o valor por dia — selecionados no calendário abaixo.
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <F label="Data de Início"><input type="date" value={form.data_inicio} onChange={e => handle("data_inicio", e.target.value)} className="inp-l"/></F>
              <F label="Duração (meses)"><input type="number" value={form.duracao_meses} onChange={e => handle("duracao_meses", parseInt(e.target.value) || 12)} className="inp-l"/></F>
            </div>
          </Card>

          {/* Identificação do Orçamento */}
          <Card title="Identificação do Orçamento" accent="#B28F4B" extra="Campos protegidos — não podem ser removidos">
            <div className="grid grid-cols-2 gap-3">
              <F label="Data do Orçamento 🔒"><input value={new Date().toLocaleDateString("pt-BR")} readOnly className="inp-l bg-slate-100"/></F>
              <F label="Número do Orçamento"><input value={form.numero_orcamento} onChange={e => handle("numero_orcamento", e.target.value)} className="inp-l"/></F>
            </div>
            <F label="Operador SOEASY Responsável pelo Documento">
              <input value={form.operador} onChange={e => handle("operador", e.target.value)} placeholder="Nome do responsável..." className="inp-l mt-3"/>
            </F>
          </Card>

          {/* Parâmetros Globais */}
          <Card title="Parâmetros Globais de Formação de Preço" accent="#22D3EE">
            <div className="grid grid-cols-2 gap-3">
              <F label="Overhead / Reserva + Admin (%)"><input type="number" step="0.01" value={form.overhead_pct} onChange={e => handle("overhead_pct", parseFloat(e.target.value) || 0)} className="inp-l"/><span className="text-[10px] text-[#6B6E8F] mt-1 block">Base recomendada: 15-25%</span></F>
              <F label="Margem de Lucro sobre NF (%)"><input type="number" step="0.01" value={form.margem_lucro_pct} onChange={e => handle("margem_lucro_pct", parseFloat(e.target.value) || 0)} className="inp-l"/><span className="text-[10px] text-[#6B6E8F] mt-1 block">Recomendado: 10-20%</span></F>
            </div>

            <div className="mt-4 bg-gradient-to-br from-[#131532] to-[#1F2350] border border-[#B28F4B]/25 rounded-xl p-4">
              <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.25em] uppercase mb-3">Composição do Preço NF — Todas as Alíquotas</div>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {ALIQUOTAS.map((a, i) => (
                  <div key={i} className="bg-[#0A0B1A]/70 border border-[#B28F4B]/20 rounded-lg p-2 text-center">
                    <div className="text-[#B28F4B] font-mono font-bold text-sm">{a.pct.toFixed(2)}%</div>
                    <div className="text-[8px] text-[#A1A4C7] uppercase tracking-widest font-bold mt-1">{a.sigla}</div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <div className="flex justify-end gap-3 pb-8">
            <button onClick={() => navigate("/sgo/orcamentos")} className="px-6 py-3 rounded-xl bg-white border border-slate-300 text-[#1A1D42] font-bold text-xs uppercase tracking-widest">Cancelar</button>
            <button onClick={salvar} disabled={saving} data-testid="btn-salvar-orcamento" className="bg-gradient-to-br from-[#B28F4B] to-[#8b6f3a] hover:from-[#D4AF37] text-[#1A1D42] px-8 py-3 rounded-xl font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2 shadow-lg disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
              Salvar Orçamento
            </button>
          </div>
        </div>

        {/* Coluna direita: mesmo padrão do Orçamentos */}
        <aside className="space-y-4">
          <div className="bg-gradient-to-br from-[#131532] to-[#1F2350] border border-[#B28F4B]/25 rounded-2xl p-5">
            <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.25em] uppercase mb-4">Resumo do Orçamento</div>
            <div className="bg-[#0A0B1A]/50 border border-[#B28F4B]/20 rounded-xl p-4 mb-3">
              <div className="text-[#A1A4C7] text-[9px] uppercase tracking-widest font-bold text-center">Faturamento Mensal · NF Total</div>
              <div className="text-[#B28F4B] font-mono text-3xl font-bold text-center mt-1">R$ 0,00</div>
            </div>
            <div className="text-white text-xs">Preencha as seções para calcular</div>
          </div>
        </aside>
      </div>

      <style>{`.inp-l{width:100%;background:#F8FAFC;border:1.5px solid #E2E8F0;color:#1A1D42;padding:10px 14px;border-radius:8px;outline:none;font-size:13px;transition:.15s} .inp-l:focus{border-color:#B28F4B;box-shadow:0 0 0 3px rgba(178,143,75,.15)}`}</style>
    </SgoShell>
  );
}

function Card({ title, accent, extra, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[#B28F4B]/15 shadow-sm p-5">
      {title && (
        <div className="flex items-center justify-between mb-4 pb-3 border-b-2" style={{ borderColor: accent }}>
          <div className="font-heading font-bold text-[#1A1D42] flex items-center gap-2">
            <span className="w-1.5 h-5 rounded-full" style={{ background: accent }}/>
            {title}
          </div>
          {extra && <div className="text-[10px] font-bold text-[#6B6E8F] uppercase tracking-widest">{extra}</div>}
        </div>
      )}
      {children}
    </div>
  );
}
function F({ label, children }) {
  return <div><label className="text-[10px] font-bold uppercase tracking-widest text-[#6B6E8F] block mb-1.5">{label}</label>{children}</div>;
}
